<script>
  // Cloudinary storage: find the pictures and videos nothing uses any more (removed from a product, replaced, never saved) and delete them.
  // "Check" only looks. "Delete" works the list out again on the server, so it deletes what is unused at that moment, not what was on screen.
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';
  import ImageOff from 'lucide-svelte/icons/image-off';
  import Film from 'lucide-svelte/icons/film';

  let scan = $state(null);
  let busy = $state('');
  let error = $state('');

  const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  async function api(method, url, body) {
    const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }
  async function check() {
    busy = 'check'; error = '';
    try { scan = await api('GET', '/api/admin/media'); }
    catch (e) { error = e.message; scan = null; }
    finally { busy = ''; }
  }
  async function cleanUp() {
    const n = scan.unused.count;
    if (!(await confirmDialog.ask({ title: `Delete ${n} unused file${n === 1 ? '' : 's'}?`, description: `This frees about ${size(scan.unused.bytes)} on Cloudinary. These files are not used by any product, post, event, lookbook, page or past order, and cannot be restored once deleted.`, confirmLabel: `Delete ${n} file${n === 1 ? '' : 's'}`, destructive: true }))) return;
    busy = 'clean'; error = '';
    try {
      const r = await api('POST', '/api/admin/media/cleanup', { confirm: true });
      toast.success(`Deleted ${r.deleted} file${r.deleted === 1 ? '' : 's'}, freeing ${size(r.bytes)}.${r.failed ? ` ${r.failed} could not be deleted: run it again.` : ''}`);
      scan = await api('GET', '/api/admin/media');
    } catch (e) { error = e.message; }
    finally { busy = ''; }
  }
</script>

<Card title="Media storage" description="Pictures and videos on Cloudinary that nothing uses any more, such as photos you removed from a product or replaced.">
  <div class="space-y-4 p-6 pt-4">
    {#if scan && !scan.configured}
      <p class="text-sm text-muted-foreground">Cloudinary is not set up on this server, so there is nothing to clean.</p>
    {:else}
      <div class="flex flex-wrap items-center gap-3">
        <Button variant="outline" onclick={check} disabled={!!busy}>{#if busy === 'check'}<LoaderCircle size={14} class="animate-spin" />{/if} {scan ? 'Check again' : 'Check for unused files'}</Button>
        {#if scan?.configured && scan.unused.count > 0 && !scan.guard}
          <Button variant="destructive" onclick={cleanUp} disabled={!!busy}>{#if busy === 'clean'}<LoaderCircle size={14} class="animate-spin" />{/if} Delete {scan.unused.count} unused file{scan.unused.count === 1 ? '' : 's'} ({size(scan.unused.bytes)})</Button>
        {/if}
      </div>
      {#if error}<p role="alert" class="text-sm text-destructive">{error}</p>{/if}
      {#if scan?.configured}
        <p role="status" class="text-sm">
          {#if scan.guard}{scan.guard}
          {:else if scan.unused.count === 0}<span class="font-medium">Nothing to clean.</span> All {scan.total.count} files ({size(scan.total.bytes)}) are in use{#if scan.recent.count}, or too new to judge ({scan.recent.count}){/if}.
          {:else}<span class="font-medium">{scan.unused.count} unused file{scan.unused.count === 1 ? '' : 's'}, {size(scan.unused.bytes)}.</span> <span class="text-muted-foreground">Of {scan.total.count} files ({size(scan.total.bytes)}), {scan.inUse.count} are in use{#if scan.recent.count}, and {scan.recent.count} uploaded in the last {scan.graceHours} hours are left alone in case they are still being edited{/if}.</span>{/if}
        </p>
        {#if scan.unused.sample.length}
          <ul class="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-8" aria-label="The largest unused files">
            {#each scan.unused.sample as a (a.id)}
              <li class="overflow-hidden rounded-md border border-border" title="{a.id} · {size(a.bytes)}">
                <div class="grid aspect-square place-items-center bg-muted">{#if a.url}<img src={a.url.replace('/upload/', '/upload/c_fill,w_160,h_160,f_auto,q_auto/')} alt="" class="h-full w-full object-cover" loading="lazy" />{:else if a.type === 'video'}<Film size={20} class="text-muted-foreground" />{:else}<ImageOff size={20} class="text-muted-foreground" />{/if}</div>
                <p class="px-1.5 py-1 text-[11px] text-muted-foreground">{size(a.bytes)}</p>
              </li>
            {/each}
          </ul>
          {#if scan.unused.count > scan.unused.sample.length}<p class="text-xs text-muted-foreground">Showing the {scan.unused.sample.length} largest.</p>{/if}
        {/if}
        <p class="text-xs text-muted-foreground">A file counts as used if it appears anywhere in your products (including colour photos), posts, events, lookbooks, pages, settings or past orders. Deleting a product, post, event or lookbook also removes its own pictures straight away.</p>
      {/if}
    {/if}
  </div>
</Card>
